import { useState, useEffect, useCallback } from "react";
import { api } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DayPicker } from "react-day-picker";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { formatDateLocal, parseDateLocal, formatDateSafe } from "@/utils/dateHelpers";
import { toast } from "sonner";
import { Plus, Plane, Calendar, Trash2, Users, CreditCard, Settings, Globe, LogOut, Building2, BookOpen, Moon, MapPin, ListChecks, Edit, Hotel, Ship, Car, Train, Bus, Activity, Shield, Inbox, Menu, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatSegmentLocationWithDetails } from "@/utils/segmentDisplay";
import { getSegmentTitle } from "@/utils/segmentTitle";
import { getCountryFlag } from "@/utils/countryFlags";
import { TripCardSkeletonList } from "@/components/skeletons";
import GlobalSearch from "@/components/GlobalSearch";
import { SegmentDurationBadge, SameDayTransitSummary } from "@/components/shared";
import { loadAirportTimezoneMap } from "@/utils/iataTimezones";
import UserSettingsDialog from "@/components/UserSettingsDialog";
// Hoisted out of the per-trip enrichment loop (was a dynamic `import()`
// on every trip — wasted ~50ms per dashboard load with no caching win).
import {
  calculatePlannedNights,
  calculateTripNights,
  calculateNightsByStatus,
} from "@/utils/tripCalculations";
import { calculateCountryVisits, getTotalDaysByCountry } from "@/utils/visitCalculator";
import TripCard from "@/components/dashboard/TripCard";
import GettingStartedCard from "@/components/dashboard/GettingStartedCard";
import { readDashboardTripsCache, writeDashboardTripsCache } from "@/utils/dashboardCache";
import { tripDataStore } from "@/hooks/useTripData";
import { useUserSettings } from "@/contexts/UserSettingsContext";

// Helper function to get the appropriate icon for segment types
const getSegmentIcon = (type, className = "h-6 w-6") => {
  switch (type) {
    case 'flight':
      return <Plane className={className} />;
    case 'accommodation':
      return <Hotel className={className} />;
    case 'cruise':
    case 'cruise_day':
    case 'cruise_port':
      return <Ship className={className} />;
    case 'train':
      return <Train className={className} />;
    case 'bus':
      return <Bus className={className} />;
    case 'car_rental':
      return <Car className={className} />;
    case 'activity':
    case 'site_plan':
    case 'event':
      return <Activity className={className} />;
    default:
      return <MapPin className={className} />;
  }
};

// Helper function to format milestone time display
const formatMilestoneTime = (segmentDateStr) => {
  if (!segmentDateStr) return "";
  
  // Handle date-only strings (YYYY-MM-DD) without timezone conversion
  let milestone;
  if (segmentDateStr.length === 10 && segmentDateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = segmentDateStr.split('-').map(Number);
    milestone = new Date(year, month - 1, day);
  } else {
    // For datetime strings, parse normally
    milestone = new Date(segmentDateStr);
  }
  
  const now = new Date();
  
  // Get start of day for both dates in user's local timezone
  const milestoneDay = new Date(milestone.getFullYear(), milestone.getMonth(), milestone.getDate());
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Calculate difference in days
  const diffTime = milestoneDay - todayDay;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return "Today!";
  } else if (diffDays === 1) {
    return "Tomorrow";
  } else if (diffDays === -1) {
    return "Yesterday";
  } else if (diffDays < -1) {
    return "Past";
  } else {
    return `${diffDays} days away`;
  }
};

// Helper function to categorize trips
const categorizeTrips = (trips) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const current = [];
  const future = [];
  const past = [];
  const archived = [];
  
  trips.forEach(trip => {
    // Parse dates as local dates to avoid timezone conversion
    const [startYear, startMonth, startDay] = trip.start_date.split('-').map(Number);
    const [endYear, endMonth, endDay] = trip.end_date.split('-').map(Number);
    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    
    if (startDate <= today && endDate >= today) {
      // Trip is currently ongoing
      current.push(trip);
    } else if (startDate > today) {
      // Trip hasn't started yet
      future.push(trip);
    } else if (endDate < today) {
      // Trip has ended
      if (endDate >= thirtyDaysAgo) {
        // Ended less than 30 days ago
        past.push(trip);
      } else {
        // Ended more than 30 days ago - archive
        archived.push(trip);
      }
    }
  });
  
  // Sort each category - use string comparison for YYYY-MM-DD format
  current.sort((a, b) => a.start_date.localeCompare(b.start_date));
  future.sort((a, b) => a.start_date.localeCompare(b.start_date));
  past.sort((a, b) => b.end_date.localeCompare(a.end_date)); // Most recent first
  archived.sort((a, b) => b.end_date.localeCompare(a.end_date)); // Most recent first
  
  return { current, future, past, archived };
};

const Dashboard = () => {
  const isMobile = useIsMobile();
  const { settings: userSettings } = useUserSettings();
  // Hydrate trips from localStorage immediately so the user sees their
  // last-known cards within milliseconds on cold start / return-to-
  // dashboard navigations. The real /dashboard/trips fetch overwrites
  // this once it lands. See utils/dashboardCache.js.
  const [trips, setTrips] = useState(() => {
    try {
      const userStr = localStorage.getItem("user");
      const uid = userStr ? JSON.parse(userStr).id : null;
      return readDashboardTripsCache(uid) || [];
    } catch {
      return [];
    }
  });
  const [travelers, setTravelers] = useState([]);
  const [nextMilestone, setNextMilestone] = useState(null);
  const [inboxCounts, setInboxCounts] = useState({ total: 0, new: 0 });
  const [paymentMethodsCount, setPaymentMethodsCount] = useState(0);
  const [gettingStartedHidden, setGettingStartedHidden] = useState(true);
  const [prefsDone, setPrefsDone] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isArchiveExpanded, setIsArchiveExpanded] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [tripSegmentCount, setTripSegmentCount] = useState(0);
  const [deleteOption, setDeleteOption] = useState("keep_segments");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingTrips, setIsLoadingTrips] = useState(true);
  const [editingTrip, setEditingTrip] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [newTrip, setNewTrip] = useState({
    name: "",
    start_date: "",
    end_date: "",
    description: ""
  });
  const [tripDateRange, setTripDateRange] = useState({ from: undefined, to: undefined });
  const [selectedTravelerIds, setSelectedTravelerIds] = useState([]);
  const navigate = useNavigate();

  // Global search keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Warm the IATA→timezone cache so the Next Milestone card can compute
  // tz-aware flight durations without a network gap on first render.
  useEffect(() => {
    loadAirportTimezoneMap();
  }, []);

  const loadCurrentUser = useCallback(async () => {
    setIsLoadingUser(true);
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        
        // Fetch fresh user data from backend to ensure we have latest account_type
        try {
          const response = await api.get(`/auth/me`);
          
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(response.data));
          setCurrentUser(response.data);
          setGettingStartedHidden(!!response.data.getting_started_hidden);
          setPrefsDone(!!response.data.onboarding_prefs_done);
          setIsLoadingUser(false);
          
          // Fetch dashboard data after user is loaded
          await Promise.all([
            fetchTrips(),
            fetchNextMilestone(),
            fetchTravelers(),
            fetchInboxCounts(),
            fetchPaymentMethodsCount()
          ]);
        } catch (error) {
          // Fall back to cached user if API fails
          setCurrentUser(user);
          setGettingStartedHidden(!!user.getting_started_hidden);
          setPrefsDone(!!user.onboarding_prefs_done);
          setIsLoadingUser(false);
          
          // Still fetch dashboard data with cached user
          await Promise.all([
            fetchTrips(),
            fetchNextMilestone(),
            fetchTravelers(),
            fetchInboxCounts(),
            fetchPaymentMethodsCount()
          ]);
        }
      } catch (error) {
        setIsLoadingUser(false);
      }
    } else {
      setIsLoadingUser(false);
    }
    // fetchTrips/fetchNextMilestone/fetchTravelers/fetchInboxCounts are stable
    // module-level helpers — including them would cause infinite re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  const isViewOnlyUser = () => {
    // Dashboards are unified for all users (June 2026). Access is enforced
    // per-trip on the backend (owner can edit; travelers/shares are view-only),
    // so the dashboard layout, header and toolbar are identical for everyone.
    return false;
  };

  const handleLogout = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        await api.post(`/auth/logout`, {});
      }
    } catch (error) {
    } finally {
      // Clear local storage regardless of API success
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      toast.success('Logged out successfully');
      navigate('/login');
    }
  };

  const fetchTravelers = async () => {
    try {
      const response = await api.get(`/travelers`);
      setTravelers(response.data);
    } catch (error) {
    }
  };

  const fetchInboxCounts = async () => {
    try {
      const response = await api.get(`/email/pending/count`);
      setInboxCounts(response.data);
    } catch (error) {
      setInboxCounts({ total: 0, new: 0 });
    }
  };

  const fetchPaymentMethodsCount = async () => {
    try {
      const response = await api.get(`/payment-methods`);
      const methods = Array.isArray(response.data) ? response.data : [];
      // Exclude the auto-seeded default "Cash" method so it doesn't count
      // toward the Getting Started "Add payment cards" milestone.
      setPaymentMethodsCount(methods.filter((m) => !m.is_default).length);
    } catch (error) {
      setPaymentMethodsCount(0);
    }
  };

  const handleDismissGettingStarted = async () => {
    setGettingStartedHidden(true);
    try {
      await api.patch(`/user/onboarding`, { getting_started_hidden: true });
    } catch (error) {
      // Non-fatal — keep it hidden locally regardless.
    }
  };

  const handleOpenSettingsFromOnboarding = async () => {
    setIsSettingsOpen(true);
    if (!prefsDone) {
      setPrefsDone(true);
      try {
        await api.patch(`/user/onboarding`, { onboarding_prefs_done: true });
      } catch (error) {
        // Non-fatal.
      }
    }
  };

  const fetchTrips = async () => {
    setIsLoadingTrips(true);
    try {
      let allTrips = [];
      let allSegmentsByTrip = {};
      let allSharesByTrip = {};
      let allTravelersByTrip = {};
      let cruiseItineraryBySegment = {};
      
      // Fetch owned trips with aggregated data (NEW: 1 call instead of 3N!)
      try {
        const ownedResponse = await api.get(`/dashboard/trips`);
        allTrips = [...ownedResponse.data.trips];
        allSegmentsByTrip = ownedResponse.data.segments_by_trip || {};
        allSharesByTrip = ownedResponse.data.shares_by_trip || {};
        allTravelersByTrip = ownedResponse.data.travelers_by_trip || {};
        // Pre-fetched cruise itineraries — eliminates per-cruise API
        // calls inside visitCalculator (huge perf win on production).
        cruiseItineraryBySegment = ownedResponse.data.cruise_itinerary_by_segment || {};
      } catch (error) {
      }
      
      // Fetch shared trips (now returns segments inline — no N+1 loop needed)
      try {
        const sharedResponse = await api.get(`/shared-with-me`);
        const sharedTrips = sharedResponse.data.shared_trips || [];

        for (const item of sharedTrips) {
          if (item.trip && !allTrips.find(t => t.id === item.trip.id)) {
            allTrips.push(item.trip);
            // Segments are pre-fetched by the backend; use them directly.
            allSegmentsByTrip[item.trip.id] = item.segments || [];
          }
        }
      } catch (error) {
      }
      
      // Progressive rendering: render the cards immediately with the
      // raw trip data, then enrich each trip in the background and
      // patch the stats in as they finish. This eliminates the
      // "blank cards for 5+ seconds while every trip is enriched in
      // parallel" wait the user was seeing on production.
      //
      // Each trip merges with any existing entry from the LS-hydrated
      // initial state — so the cards never lose their stats while the
      // refresh is in flight.
      setTrips((prev) => {
        const prevById = new Map(prev.map((t) => [t.id, t]));
        return allTrips.map((t) => {
          const existing = prevById.get(t.id);
          return existing && existing.stats ? { ...t, stats: existing.stats } : t;
        });
      });
      // Once we know the trip list, also clear any stale LS rows from
      // trips that no longer exist (deleted on another tab).
      setIsLoadingTrips(false);

      // Enrich trips with calculated stats using pre-fetched data
      const enrichedTrips = await enrichTripsWithStats(
        allTrips,
        allSegmentsByTrip,
        allSharesByTrip,
        allTravelersByTrip,
        cruiseItineraryBySegment,
      );

      // Final consistent state (handles deletions/reorders).
      setTrips(enrichedTrips);

      // Persist for instant first paint on the next visit. We only
      // cache enriched trips so the next mount renders complete cards
      // (not half-empty ones).
      try {
        const userStr = localStorage.getItem("user");
        const uid = userStr ? JSON.parse(userStr).id : null;
        writeDashboardTripsCache(uid, enrichedTrips);
      } catch {
        /* LS write failure is non-fatal */
      }
    } catch (error) {
      toast.error("Failed to load trips");
    } finally {
      setIsLoadingTrips(false);
    }
  };

  const enrichTripsWithStats = async (trips, segmentsByTrip = {}, sharesByTrip = {}, travelersByTrip = {}, cruiseItineraryBySegment = {}) => {
    // Progressive enrichment: process trips in parallel but emit
    // setTrips patches as each one finishes so the user sees stats
    // populate live instead of all-at-once after every trip is done.
    const enrichOne = async (trip) => {
      try {
        // Get segments from pre-fetched aggregated data (eliminating N+1 API calls!)
        const segments = segmentsByTrip[trip.id] || [];
        
        // Use the EXACT same values as TripDetails header (using shared utilities)
        const tripNights = calculateTripNights(trip); // Simple trip duration (start to end date)
        const plannedNights = calculatePlannedNights(segments); // Complex planned calculation (accommodations + cruises + flights)
        const planned = plannedNights; // Use plannedNights for the "Planned" badge

        // ONE country-visit calc per trip — derive both the count and
        // the names from the same result. Previously we called
        // `calculateCountries()` and `getCountryNames()` separately;
        // each independently re-fetched every cruise itinerary, which
        // doubled network round-trips on the dashboard. The pre-fetched
        // `cruiseItineraryBySegment` map means visitCalculator does
        // zero network calls now.
        let countries = 0;
        let countryNames = [];
        if (segments.length > 0) {
          try {
            const visits = await calculateCountryVisits(segments, null, cruiseItineraryBySegment);
            const totalsByCountry = getTotalDaysByCountry(visits);
            const real = totalsByCountry.filter(({ country }) => country !== "International Waters");
            countries = real.length;
            countryNames = real.map(({ country }) => country);
          } catch {
            // Fallback to simple per-segment country dedup if the
            // visit calc throws — better partial stats than none.
            const named = segments.filter(s =>
              !s.is_cancelled && !s.is_mistake &&
              (s.type === 'accommodation' || s.type === 'cruise') && s.country
            );
            countryNames = [...new Set(named.map(s => s.country))];
            countries = countryNames.length;
          }
        }

        // Calculate nights by status (calendar-grid dedup — pass the trip
        // so overlapping accommodations don't double-count nights).
        const nightsByStatus = calculateNightsByStatus(segments, trip);
        
        // Calculate pending nights (nights with no accommodation planned)
        const accountedNights = nightsByStatus.plannedNights + nightsByStatus.bookedNights + nightsByStatus.completedNights;
        const pendingNights = Math.max(0, tripNights - accountedNights);
        
        // Get shares and travelers from pre-fetched data (eliminating 2 more API calls per trip!)
        const shares = sharesByTrip[trip.id] || [];
        const hasShares = shares.length > 0 && trip.created_by_user_id === currentUser?.id;
        const designatedTravelers = travelersByTrip[trip.id] || [];
        
        return {
          ...trip,
          stats: {
            nights: tripNights, // Trip duration for "Nights" badge
            planned, // Planned nights for "Planned" badge  
            countries,
            countryNames,
            // Status breakdown
            pendingNights: pendingNights,
            plannedNights: nightsByStatus.plannedNights,
            bookedNights: nightsByStatus.bookedNights,
            completedNights: nightsByStatus.completedNights,
            totalNights: tripNights // Always use trip duration as total
          },
          hasShares, // Add flag indicating if trip has active shares
          designatedTravelers // Add designated travelers
        };
      } catch (error) {
        return {
          ...trip,
          stats: { nights: 0, planned: 0, countries: 0, countryNames: [] }
        };
      }
    };

    // Fire all enrichments in parallel. As each one resolves, splice
    // it into the trips array so the user sees the stats fill in.
    const enrichedTrips = await Promise.all(trips.map(async (trip) => {
      const enriched = await enrichOne(trip);
      // Patch the single trip into state by id — keeps the rest of
      // the list untouched (no full re-render).
      setTrips((prev) => {
        const next = prev.slice();
        const idx = next.findIndex((t) => t.id === enriched.id);
        if (idx === -1) return [...next, enriched];
        next[idx] = enriched;
        return next;
      });
      return enriched;
    }));

    return enrichedTrips;
  };


  const fetchNextMilestone = async () => {
    try {
      const now = new Date();
      const userDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const userTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      
      const response = await api.get(`/dashboard/next-milestone`, {
        headers: {
          'X-User-Date': userDate,
          'X-User-Time': userTime
        }
      });
      
      if (!response.data.message) {
        setNextMilestone(response.data);
      } else {
        setNextMilestone(null);
      }
    } catch (error) {
      setNextMilestone(null);
    }
  };

  const handleEditTrip = (e, trip) => {
    e.stopPropagation(); // Prevent card click
    setEditingTrip(trip);
    setNewTrip({
      name: trip.name,
      start_date: trip.start_date,
      end_date: trip.end_date,
      description: trip.description || ""
    });
    
    // Set date range for calendar - use parseDateLocal to avoid timezone issues
    if (trip.start_date && trip.end_date) {
      setTripDateRange({
        from: parseDateLocal(trip.start_date),
        to: parseDateLocal(trip.end_date)
      });
    }
    
    // Set designated travelers if they exist
    if (trip.designated_traveler_ids && Array.isArray(trip.designated_traveler_ids)) {
      setSelectedTravelerIds(trip.designated_traveler_ids);
    } else {
      setSelectedTravelerIds([]);
    }
    
    setIsDialogOpen(true);
  };

  const handleUpdateTrip = async () => {
    // Trim whitespace and validate
    const trimmedName = newTrip.name.trim();
    const trimmedStartDate = newTrip.start_date.trim();
    const trimmedEndDate = newTrip.end_date.trim();
    
    if (!trimmedName) {
      toast.error("Please enter a trip name");
      return;
    }
    
    if (!trimmedStartDate) {
      toast.error("Please select a start date");
      return;
    }
    
    if (!trimmedEndDate) {
      toast.error("Please select an end date");
      return;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(trimmedStartDate)) {
      toast.error("Start date must be in YYYY-MM-DD format");
      return;
    }
    
    if (!dateRegex.test(trimmedEndDate)) {
      toast.error("End date must be in YYYY-MM-DD format");
      return;
    }

    try {
      // Update trip details
      await api.put(`/trips/${editingTrip.id}`, {
        name: trimmedName,
        start_date: trimmedStartDate,
        end_date: trimmedEndDate,
        description: newTrip.description
      });
      
      // Update designated travelers
      try {
        await api.put(`/trips/${editingTrip.id}/travelers`, {
          designated_traveler_ids: selectedTravelerIds
        });
        toast.success("Trip and travelers updated successfully!");
      } catch (error) {
        toast.warning("Trip updated, but failed to update travelers");
      }
      
      setIsDialogOpen(false);
      setNewTrip({ name: "", start_date: "", end_date: "", description: "" });
      setTripDateRange({ from: undefined, to: undefined });
      setSelectedTravelerIds([]);
      const tripId = editingTrip.id;
      setEditingTrip(null);
      fetchTrips();
      // Scroll back to the updated trip card
      if (tripId) {
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="trip-card-${tripId}"]`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
      }
    } catch (error) {
      toast.error("Failed to update trip");
    }
  };

  const handleCreateTrip = async () => {
    // Trim whitespace and validate
    const trimmedName = newTrip.name.trim();
    const trimmedStartDate = newTrip.start_date.trim();
    const trimmedEndDate = newTrip.end_date.trim();
    
    if (!trimmedName) {
      toast.error("Please enter a trip name");
      return;
    }
    
    if (!trimmedStartDate) {
      toast.error("Please select a start date");
      return;
    }
    
    if (!trimmedEndDate) {
      toast.error("Please select an end date");
      return;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(trimmedStartDate)) {
      toast.error("Start date must be in YYYY-MM-DD format");
      return;
    }
    
    if (!dateRegex.test(trimmedEndDate)) {
      toast.error("End date must be in YYYY-MM-DD format");
      return;
    }

    try {
      const response = await api.post(`/trips`, {
        name: trimmedName,
        start_date: trimmedStartDate,
        end_date: trimmedEndDate,
        description: newTrip.description
      });
      
      const tripId = response.data.id;
      
      // Assign travelers to trip if any were selected
      if (selectedTravelerIds.length > 0) {
        try {
          await api.put(`/trips/${tripId}/travelers`, {
            designated_traveler_ids: selectedTravelerIds
          });
          toast.success(`Trip created with ${selectedTravelerIds.length} traveler(s)!`);
        } catch (error) {
          toast.warning("Trip created, but failed to assign travelers");
        }
      } else {
        toast.success("Trip created successfully!");
      }
      
      setIsDialogOpen(false);
      setNewTrip({ name: "", start_date: "", end_date: "", description: "" });
      setTripDateRange({ from: undefined, to: undefined });
      setSelectedTravelerIds([]);
      fetchTrips();
    } catch (error) {
      toast.error("Failed to create trip");
    }
  };

  // Use centralized date formatting utility (timezone-safe)
  const formatDate = (dateStr) => formatDateSafe(dateStr);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const names = name.trim().split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };


  const handleDeleteClick = async (e, trip) => {
    e.stopPropagation(); // Prevent card navigation
    
    // Fetch segment count for this trip
    try {
      const response = await api.get(`/segments?trip_id=${trip.id}`);
      const segmentCount = response.data.length;
      setTripSegmentCount(segmentCount);
      
      // If no segments, default to full delete (no data to lose)
      setDeleteOption(segmentCount === 0 ? "full_delete" : "keep_segments");
    } catch (error) {
      setTripSegmentCount(0);
      setDeleteOption("full_delete");
    }
    
    setTripToDelete(trip);
    setDeleteConfirmText("");
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tripToDelete) return;

    // Only require "DELETE" confirmation if trip has segments AND full delete is selected
    if (deleteOption === "full_delete" && tripSegmentCount > 0 && deleteConfirmText !== "DELETE") {
      toast.error('Please type "DELETE" to confirm');
      return;
    }

    try {
      if (deleteOption === "full_delete") {
        // Full delete - trip and all segments
        await api.delete(`/trips/${tripToDelete.id}?delete_segments=true`);
        toast.success("Trip and all segments deleted successfully");
      } else {
        // Soft delete - move segments to "Unassigned"
        await api.delete(`/trips/${tripToDelete.id}?delete_segments=false`);
        toast.success("Trip deleted. Segments moved to 'Unassigned' trip");
      }
      
      setIsDeleteDialogOpen(false);
      setTripToDelete(null);
      setDeleteConfirmText("");
      fetchTrips();
    } catch (error) {
      toast.error("Failed to delete trip");
    }
  };

  // Show loading screen until user data is loaded
  if (isLoadingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-sky-50/50 dark:from-gray-950 dark:to-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-sky-50/50 dark:from-gray-950 dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 supports-[backdrop-filter]:bg-white/95 supports-[backdrop-filter]:dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-6">
          <div className="flex justify-between items-center">
            {/* Logo — uses the user-supplied notebook artwork. Light mode
                shows the dark-blue notebook (#0F2A44 body); dark mode shows
                the white-body variant. CSS swap, no JS. */}
            <div className="flex items-center">
              <img
                src="/brand/icon-notebook-light.png"
                alt="Travel Gadjet — Plan, Track, and Manage Your Adventures"
                className="block dark:hidden h-10 md:h-12 w-auto"
                data-testid="header-logo-light"
              />
              <img
                src="/brand/icon-notebook-dark.png"
                alt="Travel Gadjet — Plan, Track, and Manage Your Adventures"
                className="hidden dark:block h-10 md:h-12 w-auto"
                data-testid="header-logo-dark"
              />
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-3">
              {!isViewOnlyUser() && (
                <>
                  <Button 
                    data-testid="travelers-link-btn"
                    variant="outline"
                    onClick={() => navigate('/travelers')}
                    className="rounded-full px-6 py-6"
                  >
                    <Users className="mr-1 h-5 w-5" />
                    Travelers
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/payment-methods')}
                    className="rounded-full px-6 py-6"
                  >
                    <CreditCard className="mr-1 h-5 w-5" />
                    Cards
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/visa-requirements')}
                    className="rounded-full px-6 py-6"
                  >
                    <Globe className="mr-1 h-5 w-5" />
                    Visas
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/vendors')}
                    className="rounded-full px-6 py-6"
                  >
                    <Building2 className="mr-1 h-5 w-5" />
                    Vendors
                  </Button>
                  {/* Research button hidden - functionality preserved for future use
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/research')}
                    className="rounded-full px-6 py-6"
                  >
                    <BookOpen className="mr-1 h-5 w-5" />
                    Research
                  </Button>
                  */}
                </>
              )}
              
              {/* User Avatar - Desktop */}
              {travelers.length > 0 && currentUser && (
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
                  {(() => {
                    if (currentUser.picture) {
                      return (
                        <button
                          onClick={() => navigate('/travelers')}
                          className="relative group"
                          title={currentUser.name}
                        >
                          <img
                            src={currentUser.picture}
                            alt={currentUser.name}
                            className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-md hover:border-sky-500 transition-all cursor-pointer"
                          />
                        </button>
                      );
                    }
                    
                    const userTraveler = travelers.find(traveler => 
                      traveler.name === currentUser.name || 
                      (traveler.email && traveler.email === currentUser.email)
                    );
                    
                    const displayTraveler = userTraveler || {
                      name: currentUser.name || currentUser.email,
                      image_url: null
                    };
                    
                    return (
                      <button
                        onClick={() => navigate('/travelers')}
                        className="relative group"
                        title={displayTraveler.name}
                      >
                        {displayTraveler.image_url ? (
                          <img
                            src={displayTraveler.image_url}
                            alt={displayTraveler.name}
                            className="h-10 w-10 rounded-full object-cover border-2 border-white shadow-md hover:border-sky-500 transition-all cursor-pointer"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white text-sm font-semibold border-2 border-white shadow-md hover:border-sky-500 transition-all cursor-pointer">
                            {getInitials(displayTraveler.name)}
                          </div>
                        )}
                      </button>
                    );
                  })()}
                </div>
              )}

              {/* Settings and Logout - Desktop */}
              {currentUser && (
                <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-300">
                  {/* Search Button */}
                  <Button 
                    variant="ghost"
                    onClick={() => setIsSearchOpen(true)}
                    className="rounded-full h-10 px-3 text-gray-600 hover:text-gray-900"
                    title="Search (⌘K)"
                  >
                    <Search className="h-5 w-5 mr-2" />
                    <span className="text-sm hidden xl:inline">Search</span>
                    <kbd className="ml-2 hidden xl:inline px-1.5 py-0.5 text-xs bg-gray-100 border rounded">⌘K</kbd>
                  </Button>
                  {!isViewOnlyUser() && (
                    <Button 
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        navigate('/email-inbox');
                        setTimeout(() => fetchInboxCounts(), 1000);
                      }}
                      className="rounded-full h-10 w-10 relative"
                      title={`Email Inbox${inboxCounts.total > 0 ? ` (${inboxCounts.total})` : ''}`}
                    >
                      <Inbox className="h-5 w-5" />
                      {inboxCounts.new > 0 && (
                        <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 px-1 bg-red-500 text-white text-xs font-bold rounded-full">
                          {inboxCounts.new}
                        </Badge>
                      )}
                      {inboxCounts.new === 0 && inboxCounts.total > 0 && (
                        <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 px-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                          {inboxCounts.total}
                        </Badge>
                      )}
                    </Button>
                  )}
                  {currentUser.is_admin && (
                    <Button 
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate('/admin')}
                      className="rounded-full h-10 w-10 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      title="Admin Portal"
                    >
                      <Shield className="h-5 w-5" />
                    </Button>
                  )}
                  <Button 
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsSettingsOpen(true)}
                    className="rounded-full h-10 w-10"
                    title="Settings"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                  <Button 
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    className="rounded-full h-10 w-10"
                    title="Logout"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                </div>
              )}
              
              {!isViewOnlyUser() && (
                <Dialog open={isDialogOpen} onOpenChange={(open) => {
                  setIsDialogOpen(open);
                  if (!open) {
                    setEditingTrip(null);
                    setNewTrip({ name: "", start_date: "", end_date: "", description: "" });
                    setTripDateRange({ from: undefined, to: undefined });
                    setSelectedTravelerIds([]);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      data-testid="create-trip-btn"
                      className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-6 py-6 rounded-full shadow-lg"
                      style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 10px 20px rgba(14, 165, 233, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                      }}
                    >
                      <Plus className="mr-1 h-5 w-5" />
                      New Trip
                    </Button>
                  </DialogTrigger>
              <DialogContent data-testid="create-trip-dialog" className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingTrip ? 'Edit Trip' : 'Create New Trip'}</DialogTitle>
                  <DialogDescription>
                    {editingTrip ? 'Update your trip details below.' : 'Start planning your next adventure. Fill in the details below.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Trip Name *</Label>
                    <Input
                      id="name"
                      data-testid="trip-name-input"
                      placeholder="e.g., European Adventure 2025"
                      value={newTrip.name}
                      onChange={(e) => setNewTrip({ ...newTrip, name: e.target.value })}
                    />
                  </div>
                  
                  {/* Traveler Selection */}
                  <div className="grid gap-2">
                    <Label>Travelers (Optional)</Label>
                    <div className="border rounded-md p-3 max-h-40 overflow-y-auto bg-gray-50">
                      {travelers.length === 0 ? (
                        <p className="text-sm text-gray-500">No travelers yet. You can add travelers later.</p>
                      ) : (
                        <div className="space-y-2">
                          {travelers.map((traveler) => (
                            <div key={traveler.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`traveler-${traveler.id}`}
                                checked={selectedTravelerIds.includes(traveler.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedTravelerIds([...selectedTravelerIds, traveler.id]);
                                  } else {
                                    setSelectedTravelerIds(selectedTravelerIds.filter(id => id !== traveler.id));
                                  }
                                }}
                              />
                              <label
                                htmlFor={`traveler-${traveler.id}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                              >
                                {traveler.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label>When *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="justify-start text-left font-normal h-auto py-2 bg-white"
                          data-testid="trip-date-picker"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {tripDateRange?.from ? (
                            tripDateRange.to && tripDateRange.from.getTime() !== tripDateRange.to.getTime() ? (
                              <>
                                {format(tripDateRange.from, 'MMM dd, yyyy')} - {format(tripDateRange.to, 'MMM dd, yyyy')}
                              </>
                            ) : (
                              format(tripDateRange.from, 'MMM dd, yyyy')
                            )
                          ) : (
                            <span className="text-muted-foreground">Select trip dates</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <DayPicker
                          mode="range"
                          selected={tripDateRange}
                          onSelect={(range) => {
                            setTripDateRange(range);
                            if (range?.from) {
                              setNewTrip({ 
                                ...newTrip, 
                                start_date: formatDateLocal(range.from),
                                end_date: range.to ? formatDateLocal(range.to) : formatDateLocal(range.from)
                              });
                            }
                          }}
                          numberOfMonths={isMobile ? 1 : 2}
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="trip-description-input"
                      placeholder="Add notes about your trip..."
                      value={newTrip.description}
                      onChange={(e) => setNewTrip({ ...newTrip, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button 
                    data-testid="submit-create-trip-btn"
                    onClick={editingTrip ? handleUpdateTrip : handleCreateTrip}
                    className="bg-sky-500 hover:bg-sky-600"
                  >
                    {editingTrip ? 'Update Trip' : 'Create Trip'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
              )}
            </div>
            
            {/* Mobile Navigation */}
            <div className="flex lg:hidden items-center gap-2">
              {/* Inbox Icon with Badge - Mobile */}
              {currentUser && !isViewOnlyUser() && (
                <Button 
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    navigate('/email-inbox');
                    setTimeout(() => fetchInboxCounts(), 1000);
                  }}
                  className="rounded-full h-10 w-10 relative"
                >
                  <Inbox className="h-5 w-5" />
                  {inboxCounts.new > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 px-1 bg-red-500 text-white text-xs font-bold rounded-full">
                      {inboxCounts.new}
                    </Badge>
                  )}
                  {inboxCounts.new === 0 && inboxCounts.total > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center p-0 px-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                      {inboxCounts.total}
                    </Badge>
                  )}
                </Button>
              )}
              
              {/* Search Button - Mobile */}
              <Button 
                variant="ghost"
                size="icon"
                onClick={() => setIsSearchOpen(true)}
                className="rounded-full h-10 w-10"
                title="Search"
              >
                <Search className="h-5 w-5" />
              </Button>

              {/* New Trip Button - Mobile (compact) */}
              {!isViewOnlyUser() && (
                <Button 
                  data-testid="create-trip-btn-mobile"
                  className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-full shadow-lg h-10 w-10 p-0"
                  onClick={() => setIsDialogOpen(true)}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              )}
              
              {/* Hamburger Menu */}
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-10 w-10">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] sm:w-[320px]">
                  <SheetHeader className="text-left pb-4 border-b">
                    <SheetTitle className="flex items-center gap-3">
                      {currentUser && (
                        <>
                          {currentUser.picture ? (
                            <img
                              src={currentUser.picture}
                              alt={currentUser.name}
                              className="h-10 w-10 rounded-full object-cover border-2 border-sky-200"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                              {getInitials(currentUser.name || currentUser.email)}
                            </div>
                          )}
                          <div className="flex flex-col">
                            <span className="text-base font-semibold">{currentUser.name?.split(' ')[0]}</span>
                            <span className="text-xs text-gray-500 font-normal">{currentUser.email}</span>
                          </div>
                        </>
                      )}
                    </SheetTitle>
                  </SheetHeader>
                  
                  <nav className="mt-6 flex flex-col gap-2">
                    {!isViewOnlyUser() && (
                      <>
                        <button
                          onClick={() => { navigate('/travelers'); setIsMobileMenuOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <Users className="h-5 w-5 text-sky-600" />
                          <span className="font-medium">Travelers</span>
                        </button>
                        <button
                          onClick={() => { navigate('/payment-methods'); setIsMobileMenuOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <CreditCard className="h-5 w-5 text-sky-600" />
                          <span className="font-medium">Cards</span>
                        </button>
                        <button
                          onClick={() => { navigate('/visa-requirements'); setIsMobileMenuOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <Globe className="h-5 w-5 text-sky-600" />
                          <span className="font-medium">Visas</span>
                        </button>
                        <button
                          onClick={() => { navigate('/vendors'); setIsMobileMenuOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <Building2 className="h-5 w-5 text-sky-600" />
                          <span className="font-medium">Vendors</span>
                        </button>
                        {/* Research button hidden - functionality preserved for future use
                        <button
                          onClick={() => { navigate('/research'); setIsMobileMenuOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <BookOpen className="h-5 w-5 text-sky-600" />
                          <span className="font-medium">Research</span>
                        </button>
                        */}
                        <button
                          onClick={() => { navigate('/email-inbox'); setIsMobileMenuOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                        >
                          <Inbox className="h-5 w-5 text-sky-600" />
                          <span className="font-medium">Email Inbox</span>
                          {inboxCounts.total > 0 && (
                            <Badge className={`ml-auto ${inboxCounts.new > 0 ? 'bg-red-500' : 'bg-blue-500'} text-white text-xs`}>
                              {inboxCounts.new > 0 ? inboxCounts.new : inboxCounts.total}
                            </Badge>
                          )}
                        </button>
                      </>
                    )}
                    
                    {currentUser?.is_admin && (
                      <button
                        onClick={() => { navigate('/admin'); setIsMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-blue-50 transition-colors text-left text-blue-600"
                      >
                        <Shield className="h-5 w-5" />
                        <span className="font-medium">Admin Portal</span>
                      </button>
                    )}
                    
                    <div className="border-t my-3"></div>
                    
                    <button
                      onClick={() => { setIsSettingsOpen(true); setIsMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                    >
                      <Settings className="h-5 w-5 text-gray-600" />
                      <span className="font-medium">Settings</span>
                    </button>
                    <button
                      onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-red-50 transition-colors text-left text-red-600"
                    >
                      <LogOut className="h-5 w-5" />
                      <span className="font-medium">Log Out</span>
                    </button>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Greeting Banner */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {getGreeting()}, {currentUser?.name?.split(' ')[0] || 'Traveler'}!
          </h1>
          <p className="text-base sm:text-xl text-gray-600 dark:text-gray-400">
            {(() => {
              // Show "Welcome to Travel Gadjet!" for new users
              // New user = no trips AND account created within last 24 hours
              if (trips.length === 0 && !isLoadingTrips && currentUser?.created_at) {
                const createdAt = new Date(currentUser.created_at);
                const hoursSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
                if (hoursSinceCreation < 24) {
                  return 'Welcome to Travel Gadjet!';
                }
              }
              return 'Welcome Back!';
            })()}
          </p>
        </div>

        {/* Getting Started checklist — helps new users set up preferences,
            dependent data (travelers/cards), and their first trip. Hidden
            once dismissed; re-enable from Settings. */}
        {currentUser && !gettingStartedHidden && (
          <GettingStartedCard
            travelers={travelers}
            trips={trips}
            paymentMethodsCount={paymentMethodsCount}
            prefsDone={prefsDone}
            selectedTravelerId={userSettings?.selected_traveler_id}
            onOpenSettings={handleOpenSettingsFromOnboarding}
            onStartTrip={() => setIsDialogOpen(true)}
            onDismiss={handleDismissGettingStarted}
            navigate={navigate}
          />
        )}

        {/* Next Milestone */}
        {nextMilestone && (
          <Card data-testid="next-milestone-card" className="mb-6 sm:mb-8 bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/40 dark:to-blue-950/40 border-2 border-sky-200 dark:border-sky-800 shadow-lg">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="flex items-center text-sky-700 text-base sm:text-lg">
                {getSegmentIcon(nextMilestone.type, "mr-2 h-5 w-5 sm:h-6 sm:w-6")}
                Next Major Milestone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-2xl font-semibold text-gray-900 break-words">                    {(nextMilestone.type === 'cruise_port' || nextMilestone.type === 'cruise_day') ? (
                      <>
                        {nextMilestone.port_name}
                        {nextMilestone.country && <span className="text-gray-600">, {nextMilestone.country}</span>}
                      </>
                    ) : (
                      <>
                        {getSegmentTitle(nextMilestone)}
                        {nextMilestone.type === 'flight' && nextMilestone.flight_number && (
                          <span className="ml-2 text-sky-600">#{nextMilestone.flight_number}</span>
                        )}
                      </>
                    )}
                  </h3>
                  <p className="text-sm sm:text-lg font-medium text-gray-700 mt-1">
                    {(nextMilestone.type === 'cruise_port' || nextMilestone.type === 'cruise_day') ? (
                      <>
                        {nextMilestone.vendor_name}
                        {nextMilestone.arrival_time && (
                          <span className="ml-2 text-xs sm:text-sm text-gray-600">
                            Arrives at {nextMilestone.arrival_time}
                          </span>
                        )}
                      </>
                    ) : (
                      formatSegmentLocationWithDetails(nextMilestone)
                    )}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-3 text-gray-700">
                    <div className="flex items-center">
                      <Calendar className="mr-2 h-4 w-4" />
                      <span className="text-sm sm:text-base">{formatDate(nextMilestone.start_datetime)}</span>
                    </div>
                    {nextMilestone.type === 'flight' && nextMilestone.departure_terminal && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-700">
                        Terminal {nextMilestone.departure_terminal}
                      </span>
                    )}
                    {nextMilestone.type === 'flight' && nextMilestone.departure_gate && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700">
                        Gate {nextMilestone.departure_gate}
                      </span>
                    )}
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                      formatMilestoneTime(nextMilestone.start_datetime) === "Today!" 
                        ? "bg-red-100 text-red-700 border border-red-300" 
                        : formatMilestoneTime(nextMilestone.start_datetime) === "Tomorrow"
                        ? "bg-amber-100 text-amber-700 border border-amber-300"
                        : "bg-sky-100 text-sky-700 border border-sky-300"
                    }`}>
                      {formatMilestoneTime(nextMilestone.start_datetime)}
                    </span>
                    {/* Travel-duration badge — surfaces only for transit milestones */}
                    <SegmentDurationBadge segment={nextMilestone} />
                  </div>
                  {/* Day-of travel breakdown — totals + per-leg list when
                      multiple transit segments share the milestone's date. */}
                  {Array.isArray(nextMilestone.same_day_transit) && (
                    <SameDayTransitSummary segments={nextMilestone.same_day_transit} />
                  )}
                  {/* Who's going — traveler avatars on this milestone */}
                  {Array.isArray(nextMilestone.travelers) && nextMilestone.travelers.length > 0 && (
                    <div className="flex items-center gap-2 mt-3" data-testid="milestone-travelers">
                      <Users className="h-4 w-4 text-sky-600 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-600">Who's going:</span>
                      <div className="flex -space-x-2">
                        {nextMilestone.travelers.map((t) => {
                          const initials = (t.name || '?')
                            .split(' ')
                            .map((n) => n[0])
                            .filter(Boolean)
                            .slice(0, 2)
                            .join('')
                            .toUpperCase();
                          return (
                            <div
                              key={t.id}
                              className="relative group"
                              title={t.name}
                              data-testid={`milestone-traveler-${t.id}`}
                            >
                              {t.image_url ? (
                                <img
                                  src={t.image_url}
                                  alt={t.name}
                                  className="w-7 h-7 rounded-full border-2 border-white shadow-sm object-cover hover:scale-110 transition-transform"
                                />
                              ) : (
                                <div className="w-7 h-7 rounded-full border-2 border-white shadow-sm bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold hover:scale-110 transition-transform">
                                  {initials}
                                </div>
                              )}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                {t.name}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  className="rounded-full w-full sm:w-auto"
                  onClick={() => navigate((nextMilestone.type === 'cruise_port' || nextMilestone.type === 'cruise_day')
                    ? `/trip/${nextMilestone.trip_id || 'unknown'}#segment-${nextMilestone.cruise_segment_id || nextMilestone.segment_id}`
                    : `/trip/${nextMilestone.trip_id}#segment-${nextMilestone.id}`
                  )}
                >
                  View Details
                  <span className="ml-2">→</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View-Only Notice */}
        {isViewOnlyUser() && (
          <Card className="mb-4 sm:mb-6 bg-yellow-50 border-2 border-yellow-200">
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-xl sm:text-2xl">🔒</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">View-Only Access</h3>
                  <p className="mt-1 text-xs sm:text-sm text-yellow-700">
                    You&apos;re viewing trips shared with you. You can view all details but cannot make changes.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trips List */}
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6 text-gray-800 dark:text-gray-200" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {isViewOnlyUser() ? 'Shared Trips' : 'Your Trips'}
          </h2>
          {/* Show skeleton ONLY when there are no cached trips to render.
              If LS hydration produced an initial list, surface it
              immediately and silently refresh in the background — much
              better perceived load on cold start / return-to-dashboard. */}
          {isLoadingTrips && trips.length === 0 ? (
            <TripCardSkeletonList count={3} />
          ) : trips.length === 0 ? (
            <Card className="text-center py-16 bg-white/80 backdrop-blur-sm">
              <CardContent>
                <Plane className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No trips yet</h3>
                <p className="text-gray-500 mb-6">Start planning your first adventure!</p>
                <Button 
                  data-testid="create-first-trip-btn"
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-sky-500 hover:bg-sky-600"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Your First Trip
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {(() => {
                const { current, future, past, archived } = categorizeTrips(trips);

                // Memoized card factory. Handler refs (`navigate`,
                // `handleEditTrip`, `handleDeleteClick`, `formatDate`)
                // come from the parent scope; since the parent re-renders
                // on every keystroke / dialog toggle, those refs are
                // unstable — but the memoized component only re-renders
                // when the `trip` reference changes (see `areEqual`).
                const renderTripCard = (trip) => (
                  <TripCard
                    key={trip.id}
                    trip={trip}
                    formatDate={formatDate}
                    currentUserId={currentUser?.id}
                    isViewOnly={isViewOnlyUser()}
                    onOpen={(tripId) => navigate(`/trip/${tripId}`)}
                    onEdit={handleEditTrip}
                    onDelete={handleDeleteClick}
                    onPrefetch={(tripId) => tripDataStore.fetch(tripId)}
                  />
                );
                
                return (
                  <div className="space-y-8">
                    {/* Current Trip Section */}
                    {current.length > 0 && (
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Ship className="h-6 w-6 text-green-600" />
                          Current Trip
                        </h2>
                        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {current.map(trip => renderTripCard(trip))}
                        </div>
                      </div>
                    )}
                    
                    {/* Future Trips Section */}
                    {future.length > 0 && (
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Plane className="h-6 w-6 text-sky-600" />
                          Future Trips
                        </h2>
                        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {future.map(trip => renderTripCard(trip))}
                        </div>
                      </div>
                    )}
                    
                    {/* Past Trips Section */}
                    {past.length > 0 && (
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Calendar className="h-6 w-6 text-gray-600" />
                          Past Trips
                        </h2>
                        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                          {past.map(trip => renderTripCard(trip))}
                        </div>
                      </div>
                    )}
                    
                    {/* Trip Archive Section (Collapsible) */}
                    {archived.length > 0 && (
                      <div>
                        <button
                          onClick={() => setIsArchiveExpanded(!isArchiveExpanded)}
                          className="w-full text-left mb-4 flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                            <BookOpen className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                            Trip Archive
                            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                              ({archived.length} archived trip{archived.length !== 1 ? 's' : ''})
                            </span>
                          </h2>
                          <div className="text-gray-600 dark:text-gray-300">
                            {isArchiveExpanded ? '▼' : '▶'}
                          </div>
                        </button>
                        {isArchiveExpanded && (
                          <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {archived.map(trip => renderTripCard(trip))}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show message if no trips at all */}
                    {current.length === 0 && future.length === 0 && past.length === 0 && archived.length === 0 && (
                      <Card className="text-center py-16 bg-white/80 backdrop-blur-sm">
                        <CardContent>
                          <Plane className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                          <h3 className="text-xl font-semibold text-gray-700 mb-2">No trips yet</h3>
                          <p className="text-gray-500 mb-6">Start planning your first adventure!</p>
                          <Button 
                            data-testid="create-first-trip-btn"
                            onClick={() => setIsDialogOpen(true)}
                            className="bg-sky-500 hover:bg-sky-600"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Create Your First Trip
                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </main>

      {/* Delete Trip Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Trip</DialogTitle>
            <DialogDescription>
              Choose how to handle this trip&apos;s segments. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {tripToDelete && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium text-gray-900">{tripToDelete.name}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(tripToDelete.start_date)} - {formatDate(tripToDelete.end_date)}
                </p>
                <p className="text-sm font-medium text-sky-600 mt-2">
                  {tripSegmentCount} segment{tripSegmentCount !== 1 ? 's' : ''} in this trip
                </p>
              </div>
            )}

            {tripSegmentCount > 0 ? (
              // Show options when trip has segments
              <RadioGroup value={deleteOption} onValueChange={setDeleteOption}>
              <div className="space-y-3">
                <div 
                  className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    deleteOption === 'keep_segments' 
                      ? 'border-sky-500 bg-sky-50' 
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setDeleteOption('keep_segments');
                  }}
                >
                  <div className="flex items-center justify-center h-5 w-5 mt-0.5">
                    <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                      deleteOption === 'keep_segments' 
                        ? 'border-sky-600 bg-sky-600' 
                        : 'border-gray-400 bg-white'
                    }`}>
                      {deleteOption === 'keep_segments' && (
                        <div className="h-2 w-2 rounded-full bg-white"></div>
                      )}
                    </div>
                  </div>
                  <RadioGroupItem value="keep_segments" id="keep_segments" className="hidden" />
                  <div className="flex-1">
                    <Label htmlFor="keep_segments" className="cursor-pointer font-medium">
                      Keep Segments (Recommended)
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      Delete only the trip. All segments will be moved to an &quot;Unassigned&quot; trip for reassignment later.
                    </p>
                  </div>
                </div>

                <div 
                  className={`flex items-start space-x-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    deleteOption === 'full_delete' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-red-200 hover:bg-red-50'
                  }`}
                  onClick={() => {
                    setDeleteOption('full_delete');
                  }}
                >
                  <div className="flex items-center justify-center h-5 w-5 mt-0.5">
                    <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                      deleteOption === 'full_delete' 
                        ? 'border-red-600 bg-red-600' 
                        : 'border-gray-400 bg-white'
                    }`}>
                      {deleteOption === 'full_delete' && (
                        <div className="h-2 w-2 rounded-full bg-white"></div>
                      )}
                    </div>
                  </div>
                  <RadioGroupItem value="full_delete" id="full_delete" className="hidden" />
                  <div className="flex-1">
                    <Label htmlFor="full_delete" className="cursor-pointer font-medium text-red-700">
                      Delete Everything
                    </Label>
                    <p className="text-sm text-red-600 mt-1">
                      ⚠️ Permanently delete the trip and ALL segments, including payments, to-dos, and visa information. This cannot be recovered.
                    </p>
                  </div>
                </div>
              </div>
            </RadioGroup>
            ) : (
              // Show simple message when trip has no segments
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  This trip has no segments. Deleting it will only remove the trip itself.
                </p>
              </div>
            )}

            {deleteOption === "full_delete" && tripSegmentCount > 0 && (
              <div className="space-y-2 pt-2">
                <Label htmlFor="confirmDelete" className="text-red-700 font-medium">
                  Type &quot;DELETE&quot; to confirm permanent deletion:
                </Label>
                <Input
                  id="confirmDelete"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type DELETE here"
                  className="border-red-300 focus:border-red-500"
                />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setTripToDelete(null);
                setDeleteConfirmText("");
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteOption === "full_delete" && tripSegmentCount > 0 && deleteConfirmText !== "DELETE"}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Settings Dialog */}
      <UserSettingsDialog 
        open={isSettingsOpen}
        onOpenChange={setIsSettingsOpen}
        travelers={travelers}
        onGettingStartedChange={(hidden) => setGettingStartedHidden(hidden)}
      />

      {/* Global Search Dialog */}
      <GlobalSearch 
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
      />
    </div>
  );
};

export default Dashboard;
